import Logging from "../core/Logging";
import BaseService, { Response } from "./BaseService";
import PendingDocumentStore from "../stores/PendingDocumentStore";

export interface DocumentUploadResponse {
  clientKey: string;
  extension: string;
  pendingDocumentId: number;
  pendingThumbnailId: number;
  uploadErrors: string[];
}

export default class DocumentService {
  public static getDocumentUrl(
    documentHandle: string | null,
    pendingDocumentId: number | null,
    documentName: string,
    downloadToken: string
  ): string {
    // The document name is included in the URL so the new tab that opens will
    // have a sensible title. Special characters need to be removed from the
    // filename to make it safe for inclusion in a URL. Rather than simply
    // encoding special characters we remove them to avoid double-encoding
    // issues from IIS. This problem with special characters is made worse
    // since file names in POSSE are not required to conform to standard
    // OS file name rules.
    const sanitizedDocumentName = documentName.replace(/[^a-zA-Z0-9._ ]/g, "");

    return (
      "dynamic/Document/GetDocument" +
      `/${documentHandle}` +
      `/${pendingDocumentId}` +
      `/${sanitizedDocumentName}` +
      `?downloadToken=${downloadToken}`
    );
  }

  public static getThumbnailUrl(
    documentHandle: string | null,
    pendingDocumentId: number | null,
    pendingThumbnailId: number | null,
    thumbnailType: string,
    downloadToken: string
  ): string {
    return (
      "dynamic/Document/GetThumbnail" +
      `/${documentHandle}` +
      `/${pendingDocumentId}/${pendingThumbnailId}` +
      `/${thumbnailType}` +
      `?downloadToken=${downloadToken}` +
      `&_dc=${new Date().getTime()}`
    );
  }

  public static uploadFiles(
    dataId: string,
    widgetName: string,
    files: File[],
    progress?: Function,
    error?: Function
  ): Promise<DocumentUploadResponse>[] {
    const batch: Promise<DocumentUploadResponse>[] = [];

    for (const file of files) {
      if (file["hasError"]) {
        continue;
      }

      const formData: FormData | null = new FormData();
      const request: XMLHttpRequest = new XMLHttpRequest();

      // Track the pending document request until upload is complete
      // Do not remove in the then block due to edge cases
      PendingDocumentStore.addPendingDocument(file["rowKey"], request);

      formData.append("file", file);
      request.upload.onprogress = (event: ProgressEvent) => {
        if (progress) {
          progress(file, event);
        }
      };

      // Because the form data is binary the args can only be
      // appended to the url.
      const result: Promise<DocumentUploadResponse> = BaseService.request(
        `Document/Upload/${dataId}/${widgetName}/${file["rowKey"]}`,
        { formData },
        "POST",
        true,
        request
      )
        .then((response: Response) => {
          const responseData = JSON.parse(
            response.responseText
          ) as DocumentUploadResponse;
          const errors: string[] = responseData.uploadErrors;

          if (errors.length) {
            if (error) {
              error(file, errors);
            }

            Logging.log(`${file.name} upload failed ${errors}`);
          }

          return responseData;
        })
        .catch(() => {
          const message = "An unexpected error occurred";

          // No error thrown if the request was intentionally aborted
          if (!PendingDocumentStore.isRequestAborted(file["rowKey"])) {
            if (error) {
              error(file, [message]);
            }

            Logging.log(`${file.name} upload failed ${message}`);

            return {
              clientKey: file["rowKey"],
              extension: "",
              pendingDocumentId: -1,
              pendingThumbnailId: -1,
              uploadErrors: [message],
            } as DocumentUploadResponse;
          } else {
            return {
              clientKey: file["rowKey"],
              extension: "",
              pendingDocumentId: -1,
              pendingThumbnailId: -1,
              uploadErrors: [],
            } as DocumentUploadResponse;
          }
        });

      batch.push(result);
    }

    return batch;
  }
}
